import React from 'react';
import _ from 'lodash';
import { useMutation, useQuery } from 'react-fetching-library';
import { Modal, Card, CardContent, CardHeader, IconButton, Stack, Alert, Button, Portal, Typography, FormControlLabel, Checkbox, Select, MenuItem } from '@mui/material';
import { SmallFormTextField } from '../../../components/small-fields/SmallFormTextField';

import { sendTemplateDocument, getTemplateDocumentsAction } from '../../../services/request';

import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { useSelector } from 'react-redux';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
};


export default function DocumentForm({ open, handleClose, setDocuments, document, setCount }) {
  const refInputFile = React.useRef(null);
  const { season } = useSelector(state => state.app);
  const [functionTypes, setFunctionTypes] = React.useState([]);
  const [fileName, setFileName] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [choices, setChoices] = React.useState([]);
  const [periodChoices, setPeriodChoices] = React.useState([]);
  const [categoryChoices, setCategoryChoices] = React.useState([]);
  const [selectAllFunctions, setSelectAllFunctions] = React.useState(false);
  const [selectAllPeriods, setSelectAllPeriods] = React.useState(false);
  const { loading, mutate } = useMutation(sendTemplateDocument);
  const optionsRequest = useQuery(getTemplateDocumentsAction('OPTIONS'));
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [periods, setPeriods] = React.useState([]);
  const [periodsNames, setPeriodsNames] = React.useState([]);
  const [zsPeriodSelected, setZsPeriodSelected] = React.useState(false);
  const [category, setCategory] = React.useState(null);
  const [resort, setResort] = React.useState(null);
  const resortChoices = [
    { label: '-', value: null },
    { label: 'MRÓWKI', value: 1 },
    { label: 'GIEWARTÓW', value: 2 }
  ];

  const handleFileSelect = e => {
    if (refInputFile.current.files[0]) {
      setFileName(refInputFile.current.files[0].name);
    } else {
      setFileName(e.target.files[0].name);
    }

  };

  const handleChangeType = e => setFunctionTypes(e.target.value);

  const handleChangePeriods = e => {
    const hasWeek = e.target.value.some(pn => {
      if (pn.includes('Tydzień')) {
        setZsPeriodSelected(true);

        return true; // Zatrzymuje iterację, gdy znajdzie pierwszy pasujący element
      }
    });
    if (hasWeek) {
      setZsPeriodSelected(true);
    } else {
      setZsPeriodSelected(false);
      setResort(null);
    }

    setPeriods(e.target.value.map(pn => periodChoices.find(pc => pc.name === pn)));
    setPeriodsNames(e.target.value);
  };

  const handleSelectAllFunctions = () => {
    setSelectAllFunctions(prev => !prev);
    setFunctionTypes(prev => !selectAllFunctions ? choices.map(c => c.value) : prev
    );
  };

  const handleSelectAllPeriods = () => {
    setSelectAllPeriods(prev => !prev);
    setPeriods(prev => !selectAllPeriods ? periodChoices : prev);
    setPeriodsNames(prev => !selectAllPeriods ? periodChoices.map(p => p.name) : prev
    );
    setZsPeriodSelected(true);
  };

  const handleSubmit = async () => {
    if (functionTypes !== '') {
      const formData = new FormData();

      const isEditing = Boolean(document);
      const selectedFile = refInputFile.current.files[0];

      if (selectedFile) {
        formData.append('file', selectedFile);
        formData.append('file_name', selectedFile.name);
      }

      formData.append('function_types', functionTypes);
      formData.append('season', season);
      formData.append('name', name);
      formData.append('description', description);
      formData.append('periods', JSON.stringify(periods));
      formData.append('category', category);
      if (resort) {
        formData.append('resort_for_agreement', resort);
      }
      const { error, payload } = await mutate(formData, isEditing ? document.id : null, isEditing ? 'PATCH' : 'POST');

      if (!error) {
        if (payload.message !== undefined) {
          setErrorMessage(payload.message);
        } else {
          setDocuments(prev => {
            if (isEditing) {
              return prev.map(doc => (doc.id === document.id ? payload : doc));
            }

            return [payload, ...prev];

          });
          if (!isEditing) {
            setCount(prev => prev + 1);
          }
          handleClose();
        }
      } else {
        setErrorMessage('Wystąpił błąd');
      }
    }
  };


  React.useEffect(() => {
    if (open === false) {
      setName('');
      setDescription('');
      setFunctionTypes([]);
      setPeriods([]);
      setPeriodsNames([]);
      setFileName('');
      setCategory(null);
      setResort(null);
      setZsPeriodSelected(false);
      setErrorMessage('');
      setSelectAllFunctions(false);
      setSelectAllPeriods(false);
      if (refInputFile.current != null) {
        refInputFile.current.value = null;
      }
    }
  }, [open]);

  React.useEffect(() => {
    if (!optionsRequest.loading && !optionsRequest.error) {
      setChoices(optionsRequest.payload.actions.GET.function_type.choices);
      setPeriodChoices(optionsRequest.payload.actions.GET.periods.choices);
      setCategoryChoices(optionsRequest.payload.actions.GET.categories.choices);
    }
  }, [optionsRequest.loading]);

  React.useEffect(() => {
    if (document) {
      setName(document.name);
      setDescription(document.description);
      setFunctionTypes(document.function_types);
      setPeriods(document.periods);
      setPeriodsNames(document.periods.map(i => i.name));
      setFileName(document.file_name);
      setCategory(document.category);
      setResort(document.resort_for_agreement);
      const hasWeek = document.periods.map(i => i.name).some(pn => {
        if (pn.includes('Tydzień')) {
          setZsPeriodSelected(true);

          return true; // Zatrzymuje iterację, gdy znajdzie pierwszy pasujący element
        }
      });
      if (hasWeek) {
        setZsPeriodSelected(true);
      }
    }
  }, [document]);

  return (
    <Portal>
      <Modal open={open} onClose={handleClose}>
        <Card sx={{ ...style, width: '500px' }}>
          <CardHeader
            title={document ? 'Edytuj plik' : 'Dodaj plik'}
            action={
              <IconButton color="primary" onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            }
          />
          <CardContent>
            <Stack spacing={3} justifyContent="flex-start" alignItems="flex-start">
              <SmallFormTextField
                name="name"
                placeholder="Nazwa dokumentu"
                value={name}
                sx={{ width: '100%' }}
                onChange={e => setName(e.target.value)}
              />
              <SmallFormTextField
                name="description"
                placeholder="Opis dokumentu"
                value={description}
                sx={{ width: '100%' }}
                onChange={e => setDescription(e.target.value)}
                multiline={true}
                rows={3}
              />
              <SmallFormTextField
                select
                multiple
                name="functionTypes"
                placeholder="Rodzaj funkcji"
                label="Rodzaj funkcji"
                onChange={handleChangeType}
                value={functionTypes}
                sx={{ width: '100%' }}
                options={choices}
                disabled={selectAllFunctions}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAllFunctions}
                    onChange={handleSelectAllFunctions}
                  />
                }
                label="Wybierz wszystkie funkcje"
              />
              <Select
                variant="outlined"
                multiple
                placeholder="Wybierz termin"
                labelId="periods"
                value={periodsNames}
                defaultValue={null}
                disabled={selectAllPeriods}
                label="Terminy"
                onChange={e => handleChangePeriods(e)}
                sx={{ width: '100%', height: '60%' }}
              >
                {periodChoices &&
                    periodChoices.map((obj, key) => (
                      <MenuItem key={key} value={obj.name}>
                        {obj.name}
                      </MenuItem>
                    ))}
              </Select>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAllPeriods}
                    onChange={handleSelectAllPeriods}
                  />
                }
                label="Wybierz wszystkie terminy"
              />
              {zsPeriodSelected && <SmallFormTextField
                select
                name="resort"
                placeholder="Ośrodek ZS"
                label="Ośrodek ZS"
                onChange={e => setResort(e.target.value)}
                value={resort}
                sx={{ width: '100%' }}
                options={resortChoices}
              />}
              <SmallFormTextField
                select
                name="category"
                placeholder="Kategoria"
                label="Kategoria"
                onChange={e => setCategory(e.target.value)}
                value={category}
                sx={{ width: '100%' }}
                options={categoryChoices}
              />
              <Stack direction="row" spacing={2} alignItems="center">
                <Button variant="contained" component="label" sx={{ textAlign: 'center' }}>
                  Wczytaj plik
                  <input type="file" hidden ref={refInputFile} onChange={handleFileSelect}/>
                </Button>
                {fileName !== ''
                  ? <Stack direction="row" spacing={1}>
                    <InsertDriveFileOutlinedIcon color="primary"/>
                    <Typography variant="subtitle2">{fileName}</Typography>
                  </Stack>
                  : null}
              </Stack>
              {errorMessage !== '' ? <Alert variant="outlined" severity="error" sx={{ width: '100%' }}>{errorMessage}</Alert> : null}
              <Button variant="contained" fullWidth onClick={handleSubmit} disabled={loading}>zapisz</Button>
            </Stack>
          </CardContent>
        </Card>
      </Modal>
    </Portal>
  );
};
